import React, { useState } from 'react';
import {
  Modal,
  PrimaryButton,
  useModalControl,
  Heading,
  Text,
} from '@weave/design-system';
import { css } from '@emotion/core';
import { theme } from '@weave/theme-original';
import { HandoffSnapshot } from '../../../handoff-hub.types';
import {
  HandoffTypes,
  handoffTypesDisplayValuesMap,
} from './handoff-metrics/handoff.constants';
import { HandoffSnapshotMetrics } from './handoff-snapshot-metrics';
import { useHandoffMetrics } from './handoff-metrics/use-handoff-metrics';
import {
  getAllMetricResults,
  getConcatenatedMetricName,
  groupFailedMetrics,
} from './handoff-metrics/handoff-metrics-helpers';
import {
  ExceptionsModal,
  SelectedExceptionsMap,
} from './modals/exceptions-modal/exceptions-modal';
import { FailedMetricsModal } from './modals/failed-metrics-modal';
import { useDebugMode } from '../../../../../debug/use-debug-mode';
import { HandoffFlowProps } from './handoff-flow.types';
import { HandoffEditPage } from './handoff-edit-page';

enum HandoffViews {
  MetricsView,
  LocationInformationView,
}

// Legacy flow: metrics checklist -> location information form -> submit.
export const LegacyHandoffFlow = ({
  snapshot,
  isUpdating,
  saveSnapshot: createOrUpdateSnapshot,
  onCancel: handleCancelClick,
  navigateToRoot,
  onSubmitSuccess: handleSubmitHandoffSuccess,
  typeSelector,
}: HandoffFlowProps) => {
  const [currentView, setCurrentView] = useState<HandoffViews>(HandoffViews.MetricsView);
  const { handoffTypeData, dependentData, refetch } = useHandoffMetrics(
    snapshot.handoffType ?? HandoffTypes.OnboardingHandoff
  );
  const exceptionsModalControl = useModalControl();
  const failedHandoffModalControl = useModalControl();
  const { isDebugModeEnabled } = useDebugMode();

  const handleNextClick = (updates: Partial<HandoffSnapshot>) =>
    createOrUpdateSnapshot(updates, () =>
      setCurrentView(HandoffViews.LocationInformationView)
    );
  const handleBackClick = (updates: Partial<HandoffSnapshot>) =>
    createOrUpdateSnapshot(updates, () => setCurrentView(HandoffViews.MetricsView));
  const handleSaveAndCloseClick = (updates: Partial<HandoffSnapshot>) =>
    createOrUpdateSnapshot(updates, navigateToRoot);

  const handleSubmitClick = async (updates: Partial<HandoffSnapshot>) => {
    const metricResults = getMetricResults();
    const groupedFailedMetrics = groupFailedMetrics(metricResults, handoffTypeData);

    if (groupedFailedMetrics.withNoExceptions.length && !isDebugModeEnabled) {
      // If there is at least one failed metric with no exceptions available, save the
      // data and show the failed metrics modal.
      createOrUpdateSnapshot(updates, failedHandoffModalControl.openModal);
    } else if (groupedFailedMetrics.withExceptions.length) {
      // If there is at least one failed metric with exceptions available, save the
      // current data and show the exceptions modal.
      createOrUpdateSnapshot(updates, exceptionsModalControl.openModal);
    } else {
      // No failed metrics so submit handoff with all required data.
      const newSnapshot = {
        ...updates,
        metrics: metricResults,
      };

      createOrUpdateSnapshot(newSnapshot, handleSubmitHandoffSuccess, true);
    }
  };

  const handleSubmitWithExceptions = async (
    selectedExceptionsMap: SelectedExceptionsMap
  ) => {
    const metricResults = getMetricResults();

    const updatedMetricResults = metricResults.map((metricResult) => {
      const concatenatedName = getConcatenatedMetricName(metricResult);
      return { ...metricResult, exception: selectedExceptionsMap[concatenatedName] };
    });

    const newSnapshot = {
      ...snapshot,
      metrics: updatedMetricResults,
    };
    createOrUpdateSnapshot(newSnapshot, handleSubmitHandoffSuccess, true);
  };

  const getMetricResults = () => {
    const metricResults = getAllMetricResults(handoffTypeData, dependentData);
    return metricResults;
  };

  const showRefreshButton = currentView === HandoffViews.MetricsView;

  return (
    <HandoffEditPage
      snapshot={snapshot}
      isUpdating={isUpdating}
      headerActions={
        showRefreshButton && (
          <PrimaryButton
            size="large"
            css={refreshButtonStyle}
            onClick={refetch}
            data-testid="refreshSnapshots"
          >
            Refresh
          </PrimaryButton>
        )
      }
    >
      {currentView === HandoffViews.MetricsView && (
        <>
          {typeSelector}
          <div css={divider} />
          <div>
            <Heading level={2}>
              {handoffTypesDisplayValuesMap[snapshot.handoffType as HandoffTypes]}{' '}
              Checklist
            </Heading>
            <Text color="light" css={{ margin: 0 }}>
              Ensure the account is ready for handoff by viewing the checklist below
            </Text>
          </div>
          <HandoffSnapshotMetrics
            handoffNameInitialValue={snapshot.handoffName ?? ''}
            handoffTypeData={handoffTypeData}
            dependentData={dependentData}
            onNextClick={handleNextClick}
            onCancelClick={handleCancelClick}
            onSaveAndClose={handleSaveAndCloseClick}
          />
        </>
      )}

      {currentView === HandoffViews.LocationInformationView && (
        <handoffTypeData.FormComponent
          state={snapshot}
          disableButtons={isUpdating}
          onBackClick={handleBackClick}
          onSaveAndClose={handleSaveAndCloseClick}
          onSubmitClick={handleSubmitClick}
        />
      )}

      <Modal {...exceptionsModalControl.modalProps} maxWidth={850}>
        <ExceptionsModal
          failedMetricsWithExceptions={
            groupFailedMetrics(getMetricResults(), handoffTypeData).withExceptions
          }
          isLoading={isUpdating}
          onSubmit={handleSubmitWithExceptions}
          onClose={exceptionsModalControl.closeModal}
        />
      </Modal>

      <Modal {...failedHandoffModalControl.modalProps} maxWidth={625}>
        <FailedMetricsModal
          failedMetrics={
            groupFailedMetrics(getMetricResults(), handoffTypeData).withNoExceptions
          }
          onClose={failedHandoffModalControl.closeModal}
        />
      </Modal>
    </HandoffEditPage>
  );
};

const refreshButtonStyle = css`
  max-width: 119px;
`;

const divider = css`
  border-top: solid 1px #d8d8d8;
  margin: ${theme.spacing(1, 0)};
`;
